import React, { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Mock exams (ROO-169): full-length timed papers for the student's band,
 * published by admins via Test Management with tags 'mock-exam' + 'year-N'.
 * Starting one deep-links into the existing timed test runner (/test?testId=…),
 * which auto-submits at the time limit; results feed the weakness profile.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
function authHeaders() {
  const token = localStorage.getItem('cleveroo_token') || localStorage.getItem('accessToken') || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}

interface MockExam {
  id: string;
  title: string;
  description: string | null;
  subject: string;
  totalMarks: number | null;
  minutes: number | null;
  questionCount: number;
}

export interface MockExamListProps {
  studentId: string;
  onBack: () => void;
  onStart: (examId: string, title: string) => void;
}

const MockExamList: React.FC<MockExamListProps> = ({ studentId, onBack, onStart }) => {
  const [exams, setExams] = useState<MockExam[]>([]);
  const [band, setBand] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/api/students/${studentId}/mock-exams`, { headers: authHeaders() })
      .then((res) => {
        if (!alive) return;
        setExams(res.data?.data?.exams || []);
        setBand(res.data?.data?.band ?? null);
      })
      .catch(() => { if (alive) setError('Could not load mock exams right now.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [studentId]);

  return (
    <div className="cvr-portal" style={{ maxWidth: 860, margin: '0 auto', padding: '24px 16px 48px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <button
          type="button"
          onClick={onBack}
          style={{ border: '1.5px solid #E5DCC3', background: '#fff', color: '#1A2A1F', borderRadius: 999, padding: '6px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
        >
          ← Dashboard
        </button>
        {band != null && (
          <span style={{ fontSize: 13, color: '#6B7A6F', fontWeight: 600 }}>Band {band}</span>
        )}
      </div>

      <h1 style={{ margin: '14px 0 4px', fontSize: 24, fontWeight: 800, color: '#1A2A1F' }}>Mock exams</h1>
      <p style={{ margin: '0 0 18px', fontSize: 14, color: '#6B7A6F' }}>
        Full-length timed papers in real exam conditions — the timer keeps running, and the paper
        submits itself when time is up. Find a quiet spot and treat it like the real day.
      </p>

      {loading && <div style={{ padding: 40, textAlign: 'center', color: '#6B7A6F' }}>Loading…</div>}
      {error && <div style={{ padding: 20, color: '#B83E24', fontSize: 14 }}>{error}</div>}

      {!loading && !error && exams.length === 0 && (
        <section style={{ background: '#fff', border: '1.5px solid #EFE6D0', borderRadius: 16, padding: '38px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 34, marginBottom: 8 }}>📝</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#1A2A1F', marginBottom: 4 }}>No mock exams yet for your level</div>
          <div style={{ fontSize: 13.5, color: '#6B7A6F' }}>New papers are added regularly — check back soon.</div>
        </section>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
        {exams.map((e) => (
          <section key={e.id} style={{ background: '#fff', border: '1.5px solid #EFE6D0', borderRadius: 16, padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#1A2A1F' }}>{e.title}</h2>
            {e.description && (
              <p style={{ margin: 0, fontSize: 13, color: '#6B7A6F' }}>{e.description}</p>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 12.5, color: '#6B7A6F', fontWeight: 600 }}>
              <span>⏱ {e.minutes || '—'} min</span>
              <span>· {e.questionCount} questions</span>
            </div>
            <button
              type="button"
              onClick={() => onStart(e.id, e.title)}
              style={{ marginTop: 'auto', border: 0, background: '#14593C', color: '#fff', borderRadius: 999, padding: '8px 16px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}
            >
              Start exam →
            </button>
          </section>
        ))}
      </div>
    </div>
  );
};

export default MockExamList;
